import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { AuthorityRule, SuggestedControl } from "@ti/core";
import { BookOpen, ChevronRight, FileText, MessageSquareQuote, ShieldAlert, ShieldCheck } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { QueryState } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState, Meter, Skeleton } from "@/components/ui/misc";
import { CopyForPlatformButton } from "@/features/profile/control-copy";
import { highlightLimits } from "@/features/profile/highlight";
import { SOURCE_KIND_LABEL } from "@/features/profile/labels";
import { Callout } from "@/features/profile/review-step";
import { q } from "@/lib/api";
import { fmtPct } from "@/lib/format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/agents/$agentId/authority")({
  component: AuthorityTab,
});

type Filter = "all" | "gaps" | "controlled";

const SOURCE_ICON: Record<AuthorityRule["source"]["kind"], ReactNode> = {
  system_prompt: <MessageSquareQuote />,
  policy_doc: <FileText />,
  rag_source: <BookOpen />,
};

function AuthorityTab() {
  const { agentId } = Route.useParams();
  const query = useQuery(q.riskProfile(agentId));
  return (
    <QueryState
      query={query}
      loading={
        <div className="flex flex-col gap-4" aria-busy="true" aria-label="Loading authority profile">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      }
    >
      {(profile) => <AuthorityView agentId={agentId} rules={profile.authority} suggestions={profile.suggestions} />}
    </QueryState>
  );
}

function sourcePhrase(kind: AuthorityRule["source"]["kind"]) {
  return kind === "system_prompt" ? "Your system prompt" : kind === "policy_doc" ? "Your policy doc" : "A retrieved source";
}

function AuthorityView({ agentId, rules, suggestions }: { agentId: string; rules: AuthorityRule[]; suggestions: SuggestedControl[] }) {
  const [filter, setFilter] = useState<Filter>("all");
  const controlled = rules.filter((r) => r.controlled);
  const gaps = rules.filter((r) => !r.controlled);

  const groups = useMemo(() => {
    const visible = rules.filter((r) => (filter === "all" ? true : filter === "gaps" ? !r.controlled : r.controlled));
    const map = new Map<string, AuthorityRule[]>();
    for (const r of visible) map.set(r.area, [...(map.get(r.area) ?? []), r]);
    return [...map.entries()];
  }, [rules, filter]);

  if (!rules.length) {
    return (
      <Card>
        <EmptyState
          icon={<FileText />}
          title="No authority rules extracted yet"
          description="Rules are extracted from the system prompt, linked policy documents and frequently retrieved RAG sources. Upload policy documents, runbooks or terms to improve extraction."
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Card className="flex flex-wrap items-center gap-x-8 gap-y-4 p-4">
        <Stat label="Rules extracted" value={rules.length} />
        <Stat label="Controlled by the platform" value={controlled.length} />
        <Stat label="No platform control" value={gaps.length} />
        <div className="flex min-w-48 flex-1 flex-col gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wide text-fg-3">Control coverage</span>
          <div className="flex items-center gap-3">
            <Meter value={controlled.length} max={rules.length} label="Share of authority rules the agent platform reported controls for" className="flex-1" />
            <span className="text-[13px] font-medium tabular">{fmtPct(controlled.length / rules.length)}</span>
          </div>
        </div>
        <div role="radiogroup" aria-label="Filter rules" className="flex items-center rounded-md bg-surface-2 p-0.5">
          {(
            [
              ["all", `All ${rules.length}`],
              ["gaps", `No control ${gaps.length}`],
              ["controlled", `Controlled ${controlled.length}`],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={filter === k}
              onClick={() => setFilter(k)}
              className={cn("h-7 cursor-pointer rounded px-2.5 text-xs font-medium tabular", filter === k ? "bg-surface text-fg shadow-sm" : "text-fg-3 hover:text-fg")}
            >
              {label}
            </button>
          ))}
        </div>
      </Card>

      {gaps.length ? (
        <Callout tone="warning" icon={<ShieldAlert />} title={`${gaps.length} rule${gaps.length === 1 ? "" : "s"} your agent is told to follow ${gaps.length === 1 ? "has" : "have"} no platform control`}>
          <ul className="mt-1 flex flex-col gap-1">
            {gaps.map((r) => (
              <li key={r.id}>
                {sourcePhrase(r.source.kind)} says <span className="text-fg">“{r.source.excerpt}”</span> but the agent platform reported no control for it.{" "}
                <span className="font-mono text-[11px] text-fg-3">{r.source.ref}</span>
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-fg-3">Each gap weighs on the Controls pillar until the platform that runs the agent&apos;s tools enforces a control for it and reports the outcomes.</p>
        </Callout>
      ) : (
        <Callout tone="good" icon={<ShieldCheck />} title="The agent platform reported controls for every extracted rule">
          Prompt and policy changes trigger re-extraction; new gaps will show up here.
        </Callout>
      )}

      {groups.length ? (
        groups.map(([area, list]) => (
          <section key={area} aria-labelledby={`area-${area}`} className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <h2 id={`area-${area}`} className="text-xs font-semibold uppercase tracking-wide text-fg-3">
                {area}
              </h2>
              <span className="text-[11px] text-fg-3 tabular">
                {list.filter((r) => r.controlled).length}/{list.length} controlled
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {list.map((r) => (
                <RuleCard key={r.id} agentId={agentId} rule={r} suggestion={suggestions.find((s) => s.authorityRuleId === r.id)} />
              ))}
            </div>
          </section>
        ))
      ) : (
        <Card>
          <EmptyState title="No rules match this filter" action={<Button variant="outline" size="sm" onClick={() => setFilter("all")}>Show all rules</Button>} />
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-medium uppercase tracking-wide text-fg-3">{label}</span>
      <span className="text-xl font-semibold tabular">{value}</span>
    </div>
  );
}

function RuleCard({ agentId, rule, suggestion }: { agentId: string; rule: AuthorityRule; suggestion?: SuggestedControl }) {
  return (
    <Card className={cn("grid gap-4 p-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]", !rule.controlled && "border-l-2 border-l-warning")}>
      <div className="flex min-w-0 flex-col gap-3">
        <p className="text-[13.5px] font-medium leading-snug text-fg">{rule.rule}</p>
        <div className="mt-auto flex flex-wrap items-center gap-2">
          {rule.controlled ? (
            <Badge tone="good">
              <ShieldCheck /> Controlled by the platform
            </Badge>
          ) : (
            <>
              <Badge tone="warning">
                <ShieldAlert /> No platform control
              </Badge>
              {suggestion ? (
                <CopyForPlatformButton control={suggestion} />
              ) : (
                <Link to="/agents/$agentId/profile" params={{ agentId }} className={buttonVariants({ variant: "ghost", size: "sm" })}>
                  Suggested controls <ChevronRight />
                </Link>
              )}
            </>
          )}
        </div>
      </div>
      <figure className="flex min-w-0 flex-col gap-1.5">
        <figcaption className="flex flex-wrap items-center gap-2 text-xs text-fg-3">
          <Badge tone="neutral">
            {SOURCE_ICON[rule.source.kind]}
            {SOURCE_KIND_LABEL[rule.source.kind]}
          </Badge>
          <span className="font-mono text-[12px] text-fg-2">{rule.source.ref}</span>
        </figcaption>
        <blockquote className="rounded-r-md border-l-2 border-accent/50 bg-surface-2 px-3 py-2 text-[13px] leading-relaxed text-fg-2">{highlightLimits(rule.source.excerpt)}</blockquote>
      </figure>
    </Card>
  );
}
